import { type FC, useCallback, useEffect, useMemo } from "react";
import { View, Text } from "@visioni/a3s-ui/components";
import { ActivityFilterRow, type CameraOption, type SiteOption } from "../ActivityFilterRow/ActivityFilterRow";
import { ActivityList } from "../ActivityList/ActivityList";
import type { ActivityItemViewModel } from "../ActivityItemCard/ActivityItemCard";
import { ActivityConfigDetails } from "../ActivityConfigDetails/ActivityConfigDetails";
import { type RouteParams, type SetRouteParams, useActivityFilters } from "../../hooks/use-activity-filters";
import { useActivityList } from "../../hooks/use-activity-list";

export type ActivityErrorClassification = {
  readonly type: "forbidden" | "error";
  readonly message: string;
};

export interface ActivityPageProps {
  readonly cameras: readonly CameraOption[];
  readonly sites: readonly SiteOption[];
  readonly sitesLoading?: boolean;
  /** Current route/query params, supplied by the host app (owns the router). */
  readonly params: RouteParams;
  /** Writes route/query params, supplied by the host app. */
  readonly setParams: SetRouteParams;
  /** Called when a camera link is pressed in an activity row. */
  readonly onCameraPress?: (item: ActivityItemViewModel) => void;
  /** Formats a raw camera key into a human-readable label (fallback when camera is not in the map). */
  readonly formatCameraKey?: (key: string) => string;
  /** Classifies a fetch error into a type and user-facing message.
   *  The host app can check for 403/ForbiddenError here and return the appropriate label. */
  readonly classifyError?: (error: Error) => ActivityErrorClassification;
}

export const ActivityPage: FC<ActivityPageProps> = ({
  cameras,
  sites,
  sitesLoading,
  params,
  setParams,
  onCameraPress,
  formatCameraKey,
  classifyError,
}) => {
  const activityFilters = useActivityFilters(params, setParams);

  const cameraNames = useMemo(() => new Map(cameras.map((c) => [c.key, c.name])), [cameras]);
  const siteNames = useMemo(() => new Map(sites.map((s) => [s.id, s.name])), [sites]);

  const activity = useActivityList({
    filters: activityFilters.committedFilters,
    cameraNames,
    siteNames,
    formatCameraKey,
    enabled: true,
  });

  const selectedSite = activityFilters.filters.siteId;

  const filteredCameras = useMemo(() => {
    if (!selectedSite) return cameras;
    return cameras.filter((c) => c.siteId === selectedSite);
  }, [cameras, selectedSite]);

  const { updateFilter } = activityFilters;
  const selectedCameraKey = activityFilters.filters.cameraKey;

  useEffect(() => {
    if (!selectedCameraKey || !selectedSite) return;
    const stillValid = filteredCameras.some((c) => c.key === selectedCameraKey);
    if (!stillValid) {
      updateFilter("cameraKey", null);
    }
  }, [selectedSite, selectedCameraKey, filteredCameras, updateFilter]);

  const renderDetails = useCallback((item: ActivityItemViewModel) => <ActivityConfigDetails item={item} />, []);

  const errorClassification = activity.error && classifyError ? classifyError(activity.error) : null;

  return (
    <View flex={1} bg="background">
      <ActivityFilterRow
        filters={activityFilters}
        cameras={filteredCameras}
        sites={sites}
        sitesLoading={sitesLoading}
      />
      {errorClassification ?
        <View flex={1} alignItems="center" justifyContent="center" px="4xl">
          <Text textStyle="sm/medium" color={errorClassification.type === "forbidden" ? "textMuted" : "danger"}>
            {errorClassification.message}
          </Text>
        </View>
      : <ActivityList
          items={activity.items}
          isLoading={activity.isLoading}
          totalCount={activity.totalCount}
          hasNextPage={activity.hasNextPage}
          isFetchingNextPage={activity.isFetchingNextPage}
          onLoadMore={activity.fetchNextPage}
          renderDetails={renderDetails}
          {...(activity.error ? { error: true } : {})}
          {...(onCameraPress ? { onCameraPress } : {})}
        />
      }
    </View>
  );
};
